import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  Paper,
  BottomDrawer,
  ConfirmDialog,
  DetailField,
  EntityDetailShell,
  formatAddress,
} from '@vipso/shared';
import {
  useGetVendorQuery,
  useAddUpdateVendorMutation,
  useDeleteVendorMutation,
} from '../api/tenantApi';
import {
  VendorForm,
  EMPTY_VENDOR_FORM,
  vendorFormValues,
  type VendorFormValues,
} from '../features/vendors/VendorForm';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import { EntityApprovalPanel } from '../components/EntityApprovalPanel';

export function VendorDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetVendorQuery(id!, { skip: !id });
  const [save, { isLoading: saving }] = useAddUpdateVendorMutation();
  const [remove, { isLoading: deleting }] = useDeleteVendorMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState<VendorFormValues>(EMPTY_VENDOR_FORM);

  const vendor = data?.getVendor;

  function openEdit() {
    if (!vendor) return;
    setForm(vendorFormValues(vendor));
    setEditOpen(true);
  }

  async function handleSave() {
    if (!vendor) return;
    await save({ ...form, id: vendor.id }).unwrap();
    setEditOpen(false);
  }

  async function handleDelete() {
    if (!vendor) return;
    await remove(vendor.id).unwrap();
    setConfirmOpen(false);
    navigate('/vendors');
  }

  return (
    <EntityDetailShell
      isLoading={isLoading}
      notFound={isError || !vendor}
      notFoundTitle={t('vendors.notFound')}
      backLabel={t('vendors.backToList')}
      onBack={() => navigate('/vendors')}
      title={vendor?.name ?? ''}
      description={t('vendors.details')}
      actions={
        <Button variant="light" onClick={openEdit}>
          {t('common.edit')}
        </Button>
      }
    >
      <Paper p="lg" withBorder radius="md">
        <Stack gap="md">
          <DetailField label={t('common.phone')} value={vendor?.phone} />
          <DetailField label={t('common.email')} value={vendor?.email} />
          <DetailField
            label={t('approval.status')}
            value={<ApprovalStatusBadge status={vendor?.approvalStatus} />}
          />
          <DetailField label={t('vendors.openingBalance')} value={vendor?.openingBalance ?? 0} />
          <DetailField label={t('common.address')} value={formatAddress(vendor?.address)} />
        </Stack>
      </Paper>

      <EntityApprovalPanel
        subjectType="VENDOR"
        subjectId={vendor?.id}
        entityApprovalStatus={vendor?.approvalStatus}
      />

      <Button
        onClick={() => navigate(`/orders?vendorIds=${vendor?.id}`)}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('common.transactions')}
      </Button>

      <BottomDrawer opened={editOpen} onClose={() => setEditOpen(false)} title={t('vendors.edit')}>
        <Stack gap="md">
          <VendorForm value={form} onChange={setForm} />
          <Group justify="space-between">
            <Button color="red" variant="light" onClick={() => setConfirmOpen(true)}>
              {t('common.delete')}
            </Button>
            <Group>
              <Button variant="default" onClick={() => setEditOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button loading={saving} onClick={() => void handleSave()}>
                {t('common.save')}
              </Button>
            </Group>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleDelete()}
        title={t('vendors.deleteConfirm')}
        message={t('common.deleteMessage', { name: vendor?.name ?? '' })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </EntityDetailShell>
  );
}
